import { format, isValid, parseISO } from "date-fns";

/**
 * Date formatting helpers pinned to US conventions (MM/DD/YYYY).
 *
 * Use these instead of ad-hoc `toLocaleString` / `date-fns format` calls in
 * admin-facing UIs so dates render consistently regardless of the user's
 * browser locale and we don't keep duplicating the same format strings.
 */

export type DateInput = Date | string | number | null | undefined;

const toDate = (input: DateInput): Date | null => {
    if (input === null || input === undefined || input === "") return null;
    if (input instanceof Date) return isValid(input) ? input : null;
    if (typeof input === "string") {
        const parsed = parseISO(input);
        if (isValid(parsed)) return parsed;
        const fallback = new Date(input);
        return isValid(fallback) ? fallback : null;
    }
    if (typeof input === "number") {
        const d = new Date(input);
        return isValid(d) ? d : null;
    }
    return null;
};

/**
 * `MM/DD/YYYY, hh:mm AM/PM` — e.g. `05/21/2026, 06:08 AM`.
 * Returns "Never" for null/empty, "Invalid date" for unparseable input.
 */
export const formatUsDateTime = (input: DateInput): string => {
    const d = toDate(input);
    if (input === null || input === undefined || input === "") return "Never";
    if (!d) return "Invalid date";
    return format(d, "MM/dd/yyyy, hh:mm a");
};

/**
 * `MM/DD/YYYY` — e.g. `05/21/2026`.
 * Returns "Never" for null/empty, "Invalid date" for unparseable input.
 */
export const formatUsDate = (input: DateInput): string => {
    const d = toDate(input);
    if (input === null || input === undefined || input === "") return "Never";
    if (!d) return "Invalid date";
    return format(d, "MM/dd/yyyy");
};
